import type { MiddlewareAPI } from 'redux';
import 'rxjs/add/operator/catch';
import type { Epic, ActionsObservable } from 'redux-observable';
import type { Observable } from 'rxjs/Observable';
import type { AnalyticsAttributes } from '@atlassian/jira-product-analytics-bridge';
import fireErrorAnalytics from './fire-error-analytics.tsx';

type FamaEpicWrapperPayload<S, A, _D> = {
	attributes?: AnalyticsAttributes;

	epic: Epic<A, S>;
	errorObservable?: Observable<A>;
	id: string;
	packageName: string;
	sendToPrivacyUnsafeSplunk?: boolean;
	teamName?: string;
};

/**
 * Catches errors within the epic middleware and makes them available to monitoring.
 *
 * As opposed to epic-wrapper, this function uses fireErrorAnalytics to make data also available in SignalFx.
 *
 * Takes an optional observable as a parameter that can be returned when an error is encountered.
 * This could be used to show an error flag, for example, but will replace attempting to continue
 * the current observable stream.
 *
 * Example usage, which could be applied to the root epic:
 *
 * famaErrorHandlerEpicMiddleware({
 *   packageName: 'yourPackageName',
 *   id: 'yourErrorId',
 *   epic: combineEpics(epicOne, epicTwo),
 * );
 */

// eslint-disable-next-line jira/import/no-anonymous-default-export
export default <
		S,
		A extends {
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
			type: any;
		},
		D,
	>({
		attributes,
		epic,
		errorObservable,
		id,
		packageName,
		sendToPrivacyUnsafeSplunk,
		teamName,
	}: FamaEpicWrapperPayload<S, A, D>): Epic<A, S> =>
	(action$: ActionsObservable<A>, store: MiddlewareAPI<S>, deps: D) =>
		epic(action$, store, deps).catch((error, stream) => {
			const defaultAttributes: AnalyticsAttributes = {
				message: 'Unhandled error in epic',
			};

			fireErrorAnalytics({
				error,
				meta: {
					id,
					packageName,
					teamName,
				},
				attributes: {
					...defaultAttributes,
					...attributes,
				},
				sendToPrivacyUnsafeSplunk,
			});

			return errorObservable || stream;
		});
